/**
 * /table · SearchField
 *
 * Pill-shaped search input with a glyph (⌕) on the left and a black
 * "Find the table" button on the right. Underneath, a thin
 * "listening" line shows either the live intent (when typing) or
 * the resolved intent envelope (after search). All hint copy is
 * deterministic — never LLM-generated.
 */
import React, { useState, useMemo } from "react";
import { classifyQuery, envelopeToIntent } from "./query-intent";
import { Typeahead, buildSuggestions } from "./Typeahead";

// Cheap session-token generator. Google's Places Autocomplete bills
// per *session*, not per keystroke. We mint one token per mount of
// the search field; if the member submits or picks a suggestion
// elsewhere we just keep the same token — billing tolerates that.
function newSessionToken() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return "lc-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function SearchField({
    value,
    onChange,
    onSubmit,
    large = false,
    envelope = null,
    submittedQuery = "",
}) {
    const [focus, setFocus] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    // One Places-Autocomplete session token per mount. New token
    // on every search submission would cost ~3x more; this keeps
    // typical sessions to a single billable unit.
    const [sessionToken, setSessionToken] = useState(() => newSessionToken());
    const live = value ? classifyQuery(value) : null;
    const settled = envelope ? envelopeToIntent(envelope, submittedQuery) : null;
    const hint = focus || !settled ? live : settled;

    // Suggestions list — local fast-path only here so keyboard nav
    // stays responsive; the Typeahead component itself shows a
    // merged list (server + local). Arrow keys index against the
    // local list — first server suggestions are picked via mouse.
    const suggestions = useMemo(
        () => (focus ? buildSuggestions(value || "") : []),
        [focus, value],
    );

    const handleKeyDown = (e) => {
        if (!suggestions.length) {
            if (e.key === "Escape") setHighlight(-1);
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => (h + 1) % suggestions.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === "Enter" && highlight >= 0) {
            e.preventDefault();
            const picked = suggestions[highlight];
            onChange?.(picked.fill);
            setHighlight(-1);
            onSubmit?.(picked.fill);
        } else if (e.key === "Escape") {
            setHighlight(-1);
        }
    };

    return (
        <div className="w-full" data-testid="table-search" style={{ position: "relative" }}>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    onSubmit?.(value);
                }}
                className={[
                    "relative transition-all duration-300",
                    // Mobile: stack input + button so the placeholder
                    // never collides with the CTA (Item 19 iOS polish).
                    // ≥sm we go back to the single-row pill.
                    "flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-0",
                    focus ? "t-focusring" : "",
                ].join(" ")}
                style={{
                    background: "var(--t-card)",
                    border: `1.5px solid ${focus ? "var(--t-brass)" : "var(--t-line2)"}`,
                    borderRadius: 18,
                    // Mobile: a touch more breathing room around the
                    // stacked input + CTA. Desktop: the tight pill.
                    // (No `sm:` tokens inside inline style — browser
                    // ignores those · use the className for the
                    // desktop override.)
                    padding: large ? "10px 14px" : "8px 12px",
                }}
            >
                <div className="flex items-center flex-1 min-w-0">
                    <span
                        aria-hidden="true"
                        style={{
                            color: focus ? "var(--t-brass)" : "var(--t-ink3)",
                            fontSize: 18,
                            paddingLeft: 4,
                        }}
                    >
                        ⌕
                    </span>
                    <input
                        data-testid="table-search-input"
                        type="search"
                        value={value}
                        onChange={(e) => { onChange?.(e.target.value); setHighlight(-1); }}
                        onFocus={() => setFocus(true)}
                        onBlur={() => setTimeout(() => setFocus(false), 150)}
                        onKeyDown={handleKeyDown}
                        placeholder="Try: best omakase Tokyo · Michelin under £100 London · late-night Mumbai"
                        autoComplete="off"
                        className="flex-1 min-w-0 bg-transparent outline-none px-3"
                        style={{
                            fontSize: large ? 17 : 15,
                            color: "var(--t-ink)",
                            fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                            border: 0,
                        }}
                    />
                    {value ? (
                        <button
                            type="button"
                            data-testid="table-search-clear"
                            onClick={() => onChange?.("")}
                            aria-label="Clear search"
                            style={{
                                color: "var(--t-ink3)",
                                padding: "0 8px",
                                fontSize: 18,
                                background: "transparent",
                                border: 0,
                            }}
                        >
                            ×
                        </button>
                    ) : null}
                </div>
                <button
                    type="submit"
                    data-testid="table-search-submit"
                    style={{
                        background: "var(--t-ink)",
                        color: "var(--t-paper2)",
                        borderRadius: 14,
                        // Mobile: full-width below the input · 44px tap target.
                        padding: large ? "13px 24px" : "11px 20px",
                        minHeight: 44,
                        fontSize: 14,
                        fontWeight: 600,
                        letterSpacing: "0.01em",
                        border: 0,
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                    }}
                    // Tailwind classes own the responsive width — inline
                    // `width: 100%` previously beat `sm:w-auto` on desktop
                    // and made the input collapse to zero width
                    // (founder ear-test 5 Jun 2026).
                    className="w-full sm:w-auto"
                >
                    Find the table
                </button>

                {/* Typeahead overlay · sits absolutely below the search
                   pill · keyboard + mouse navigable. Inside the form
                   because the form already has `position: relative`,
                   so `top: 100%` anchors to the pill's bottom edge. */}
                <Typeahead
                    query={value}
                    visible={focus}
                    highlightIndex={highlight}
                    onHover={setHighlight}
                    sessionToken={sessionToken}
                    onPick={(s) => {
                        onChange?.(s.fill);
                        setHighlight(-1);
                        // Mint a new session token after a successful
                        // pick · the next typing session is a NEW
                        // billable unit per Google's session rules.
                        setSessionToken(newSessionToken());
                        onSubmit?.(s.fill);
                    }}
                />
            </form>

            <div
                className="h-6 mt-3 flex items-center gap-2 px-1"
                style={{ fontSize: 12.5 }}
                data-testid="table-listening-line"
            >
                {hint ? (
                    <span className="t-fade flex items-center gap-2" style={{ color: "var(--t-ink2)" }}>
                        <span
                            className="t-mono"
                            style={{
                                fontSize: 10,
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                color: "var(--t-brass)",
                                border: "1px solid var(--t-brass2)",
                                borderRadius: 20,
                                padding: "2px 8px",
                            }}
                        >
                            {hint.label}
                        </span>
                        <span style={{ color: "var(--t-ink3)" }}>understood as</span>
                        <span className="t-serif italic" style={{ color: "var(--t-ink)" }}>
                            {hint.detail}
                        </span>
                    </span>
                ) : (
                    <span
                        className="t-pulse"
                        style={{ color: "var(--t-ink3)" }}
                    >
                        the Concierge® is listening…
                    </span>
                )}
            </div>
        </div>
    );
}
